import React, { PureComponent } from 'react'
import Button from '../../../../Shared/Form/Button'
import Modal from '../../../../Shared/Modal'
import { getRoomName } from '../common'
import Alert from '../../../../Shared/Alert'
import './style.scss'
import { deleteRoom, getFactsheetBlueprint } from '../../../../../api'
import Loader from '../../../../Shared/Loader'
import get from 'lodash.get'
import { getFactInstanceName } from '../../../../Shared/Factsheet/helper'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'
import { SHAPE_ROOM } from '../shapes'
import HotelierPropertyService from '../../../../../services/hotelierPropertyService'
import activityTrackingService from '../../../../../services/activityTrackingService'
import { trackingEvent } from '../../../../../constants'

class DeleteRoomModal extends PureComponent {

  abortController = null
  state = {
    giataHotelierProperty: null,
    factTree: null,
    errorLoading: null,
    errorDeleting: null,
    loading: false,
    initialized: false
  }

  componentDidMount () {
    this.init()
  }

  componentWillUnmount () {
    if (this.abortController) {
      this.abortController.abort()
    }
  }

  init () {
    this.setState({
      errorLoading: null,
      errorDeleting: null,
      giataHotelierProperty: null,
      factsheet: null,
      loading: true
    }, async () => {
      if (this.abortController !== null) {
        this.abortController.abort()
      }
      this.abortController = new AbortController()
      const requests = []
      let request = HotelierPropertyService.getGiataHotelierProperty(this.props.giataId, this.abortController)
      requests.push(request)
      request = getFactsheetBlueprint(this.abortController)
      requests.push(request.fetchPromise)
      let responses
      try {
        responses = await Promise.all(requests)
      } catch (e) {
        if (this.abortController.signal.aborted) {
          return
        }
        this.setState({ loading: false, errorLoading: true, errorDeleting: null })
        return
      }
      if (responses[0].initialFactsheet) {
        responses[0].giataHotelierProperty.factsheet = responses[0].initialFactsheet
      }
      this.setState({
        giataHotelierProperty: responses[0].giataHotelierProperty,
        factTree: responses[1].factTree,
        loading: false,
        initialized: true
      })
    })
  }

  isHidingAllowed () {
    return !this.state.loading
  }

  onConfirmClick = () => {
    if (this.abortController) {
      this.abortController.abort()
    }
    activityTrackingService.trackEvent(trackingEvent.deleteRoom)
    this.abortController = new AbortController()
    this.setState({ loading: true, errorDeleting: false }, () => {
      const { fetchPromise } = deleteRoom(this.props.giataId, this.props.room.id, this.abortController)
      fetchPromise.then(() => {
        const imagesRelatedToCurrentRoomMap = new Map(
          this.getImagesRelatedToCurrentRoom().map(image => [image['id'], image])
        )
        const newImages = [...this.props.images]
        for (let i = 0; i < newImages.length; i++) {
          if (imagesRelatedToCurrentRoomMap.has(newImages[i].id)) {
            const newImage = { ...newImages[i] }
            newImage.rooms = newImage.rooms.filter(v => v !== this.props.room.id)
            newImages[i] = newImage
          }
        }
        this.setState({ loading: false, errorDeleting: null }, () => {
          this.props.onRoomRemoved(this.props.room)
          this.props.onImagesChange(newImages)
        })
      }).catch(() => {
        if (this.abortController.signal.aborted) {
          return
        }
        this.setState({ loading: false, errorDeleting: true })
      })
    })
  }

  renderFacts () {
    let facts = get(this.state.giataHotelierProperty, 'factsheet.facts', {})
    const appliesToFactInstances = []
    Object.values(facts).forEach((factInstances) => {
      factInstances.forEach((factInstance, factInstanceKey) => {
        if (factInstance.hasOwnProperty('appliesTo') && factInstance.appliesTo.includes(this.props.room.id)) {
          appliesToFactInstances.push({ key: factInstanceKey, instance: factInstance, })
        }
      })
    })
    if (appliesToFactInstances.length === 0) {
      return <Alert type={'info'}>{this.props.t('ROOM_HAS_NO_FACT_RELATIONS')}</Alert>
    }
    return (
      <div className={'facts-separated-comma'}>
        {
          appliesToFactInstances.map((v) => {
            const defaultLabel = get(
              this.state.factTree,
              'facts.' + v.instance.factDefId + '.label', '-'
            )
            return <span key={v.instance.factDefId + '-' + v.key}>
              {getFactInstanceName(v.instance, v.key, defaultLabel)}
            </span>
          })
        }
      </div>
    )
  }

  onEscapeKeyDown = () => {
    this.isHidingAllowed() && this.props.onEscapeKeyDown()
  }

  onBackdropClick = () => {
    this.isHidingAllowed() && this.props.onBackdropClick()
  }

  onCancel = () => {
    this.isHidingAllowed() && this.props.onCancel()
  }

  getImagesRelatedToCurrentRoom () {
    return this.props.images.filter((image) => {
      return image.rooms.includes(this.props.room.id)
    })
  }

  render () {
    const { t } = this.props
    const images = this.getImagesRelatedToCurrentRoom()
    return (
      <Modal show={this.props.show}
             onEscapeKeyDown={this.onEscapeKeyDown}
             onBackdropClick={this.onBackdropClick}>
        <div className={'delete-room-modal'}>
          {this.state.loading && <Loader size={32}/>}
          {(this.state.errorDeleting || this.state.errorLoading) &&
            <Alert type={'danger'}>Error</Alert>
          }
          <p className={'modal-message'}>
            {t('DELETE_ROOM_MESSAGE', {
              roomName: getRoomName(this.props.roomAttributes, this.props.room)
            })}</p>
          {this.state.initialized &&
            <React.Fragment>
              <Alert type={'warning'}>{t('DELETE_ROOM_MESSAGE_IMPLICATION')}</Alert>
              <div>
                <div>
                  <div>{t('IMAGES')}:</div>
                  <div>
                    {
                      images.length > 0 ?
                        images.map((image) => {
                          return <span key={image.id}>
                    <img src={image.thumbnailUrl} height={50} title={image.filename} alt={image.filename}/> </span>
                        }) :
                        <Alert type={'info'}>{t('ROOM_HAS_NO_IMAGE_RELATIONS')}</Alert>
                    }
                  </div>
                </div>
                <div>
                  <div>{t('FACTS')}:</div>
                  {this.renderFacts()}
                </div>
              </div>
              <Button classNames={'Button-Light'} align={'right'}
                      onClick={this.onCancel}>{t('BUTTON_CANCEL')}</Button>
              <Button align={'right'}
                      onClick={this.onConfirmClick}>{t('BUTTON_CONFIRM')}</Button>
            </React.Fragment>
          }
        </div>
      </Modal>
    )
  }
}

DeleteRoomModal.propTypes = {
  show: PropTypes.bool.isRequired,
  giataId: PropTypes.number.isRequired,
  room: PropTypes.shape(SHAPE_ROOM).isRequired,
  roomAttributes: PropTypes.any.isRequired,
  images: PropTypes.array.isRequired,
  onEscapeKeyDown: PropTypes.func.isRequired,
  onBackdropClick: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onRoomRemoved: PropTypes.func.isRequired,
  onImagesChange: PropTypes.func.isRequired
}
export default withTranslation()(DeleteRoomModal)
